import useCurrentUserClients from 'Modules/Plans/Clients/hooks/useCurrentUserClients';
import { GenericDropdownOption } from 'App.types';
import { creatorbase } from '@round/api';
import { Select } from '@round/whitelabel-ui';
import useClientSelect from '../../hooks/useClientSelect';
import { StylesConfig, ValueType } from 'react-select';
import { useCheckUserGroupsAccess } from 'Modules/Auth/hooks/useCheckUserGroupsAccess';
import { useLocation, useNavigate } from 'react-router-dom';
import { CREATORBASE_ROOT_PATH, isReportPage, isProjectDetailsPage } from 'Modules/Navigation/whitelabel/routes';
import { redirectToApp } from 'Modules/Navigation/whitelabel/appRouting';

export type ClientOption = GenericDropdownOption<number> & creatorbase.Client;

const selectStyles: StylesConfig = {
    control: (base, state) => ({
        ...base,
        padding: 0,
        background: 'transparent',
        color: 'white',
        border: 'none',
        cursor: state.isDisabled ? base.cursor : 'pointer',
        ':hover': {
            padding: 0,
            border: 'none',
        },
    }),
    input: (base) => ({
        ...base,
        color: 'white',
    }),
    singleValue: (base) => ({
        ...base,
        color: 'white',
    }),
    indicatorsContainer: (base, { isDisabled }) => ({
        ...base,
        display: isDisabled ? 'none' : base.display,
        color: 'white',
        ':hover': {
            color: 'white',
        },
    }),
    menu: (base) => ({
        ...base,
        width: '12.5rem',
    }),
    option: (base) => ({
        ...base,
        fontWeight: 500,
        fontSize: '0.75rem',
        // text-base color
        color: '#242424',
    }),
};

const ClientSelect = () => {
    const { clients, status, error } = useCurrentUserClients();
    const { selectedClientId, setSelectedClientId } = useClientSelect();
    const isCreatorbaseAdmin = useCheckUserGroupsAccess(['creatorbase_admin'], 'all');
    const isDisabled = clients.length <= 1 || !isCreatorbaseAdmin;
    const location = useLocation();
    const navigate = useNavigate();

    const options: ClientOption[] = clients.map((c) => ({
        value: c.id,
        label: c.name,
        ...c,
    }));

    // In some cases client id is selected automatically from project, report or any other entity.
    // e.g. If navigated to a project from a different client via link or from round dashboard etc.
    // In case user manually changes a client we should navigate to the list pages to maintain
    // viewed page - selected client consistency.
    const handleChange = (o: ValueType<ClientOption, false>) => {
        // Agency clients live in the agency app; selecting one bounces there (carrying the selection)
        // rather than switching client within this app.
        if (o?.is_agency) {
            redirectToApp('agency', { sessionClientId: o.value });
            return;
        }

        setSelectedClientId(o?.value ?? null);
        if (isProjectDetailsPage(location.pathname)) {
            navigate(`/${CREATORBASE_ROOT_PATH}/projects/`, { replace: true });
        }

        if (isReportPage(location.pathname)) {
            navigate(`/${CREATORBASE_ROOT_PATH}/reports/`, { replace: true });
        }
    };

    return (
        <Select
            isClearable={false}
            styles={selectStyles}
            isDisabled={isDisabled}
            isLoading={status === 'loading'}
            options={options}
            value={options.find((o) => o.value === selectedClientId)}
            onChange={handleChange}
            noOptionsMessage={() => error || 'No clients found'}
        />
    );
};

export default ClientSelect;
